// src/components/Pages/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import './../../style/Vars.css';
import './../../style/ElementsStyle.css';
import './../../style/ContainersStyles.css';
import { isMobile, isTablet } from 'react-device-detect';
import bgLogin from './img/bglogin.png';
import { FaLock, FaUser, FaEye, FaEyeSlash, FaCheckCircle, FaKey, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../../api';

import Header from './Header';
import Footer from './Footer';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    // Steps: 1 = Validar Token, 2 = Nueva Contraseña, 3 = Éxito
    const [step, setStep] = useState(1);

    const [token, setToken] = useState('');
    const [nombreUsuario, setNombreUsuario] = useState('');
    const [nuevaContrasena, setNuevaContrasena] = useState('');
    const [confirmarContrasena, setConfirmarContrasena] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [mensaje, setMensaje] = useState('');
    const [error, setError] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        // 1. Verificar si viene nombre de usuario desde ForgotPassword
        if (location.state?.nombreUsuario) {
            setNombreUsuario(location.state.nombreUsuario);
        }

        // 2. Verificar si hay mensaje inicial (ej. "Correo enviado...")
        if (location.state?.mensajeInicial) {
            setMensaje(location.state.mensajeInicial);
        }

        // 3. Verificar si viene token por URL (legacy o link directo)
        const tokenParam = searchParams.get('token');
        if (tokenParam) {
            setToken(tokenParam);
            // Si hay token y usuario, podríamos intentar validar directo o dejar que el usuario de click
        }
    }, [location.state, searchParams]);

    const containerClass = isMobile || isTablet
        ? 'login-form-container login-form-container-mobile'
        : 'login-form-container';
    const cardClass = isMobile || isTablet
        ? 'login-form-card login-form-card-mobile'
        : 'login-form-card';
    const inputClass = isMobile || isTablet
        ? 'login-form-input login-form-input-mobile'
        : 'login-form-input';
    const buttonClass = isMobile || isTablet
        ? 'login-form-button login-form-button-mobile'
        : 'login-form-button';

    const containerStyle = {
        backgroundImage: `url(${bgLogin})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
    };

    // --- MANEJADORES ---

    const handleValidarToken = async () => {
        setError('');
        setMensaje('');

        if (!nombreUsuario.trim() || !token.trim()) {
            setError('Usuario y Código son obligatorios');
            return;
        }

        setEnviando(true);

        try {
            const response = await axios.post(`${API_BASE}/RecuperacionContrasena/validar`, {
                token: token.trim(),
                nombreUsuario: nombreUsuario.trim()
            });

            if (response.data.exito) {
                setStep(2); // Pasar al siguiente paso
                setMensaje('');
            } else {
                setError(response.data.mensaje || 'Código inválido o expirado');
            }
        } catch (err) {
            console.error(err);
            if (err.response?.data?.mensaje) {
                setError(err.response.data.mensaje);
            } else {
                setError('Error al validar el código. Verifica que sea correcto.');
            }
        } finally {
            setEnviando(false);
        }
    };

    const handleActualizarPassword = async () => {
        setError('');
        setMensaje('');

        if (!nuevaContrasena || !confirmarContrasena) {
            setError('Ingresa y confirma tu nueva contraseña');
            return;
        }

        if (nuevaContrasena.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (nuevaContrasena !== confirmarContrasena) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setEnviando(true);

        try {
            const response = await axios.post(`${API_BASE}/RecuperacionContrasena/resetear`, {
                token: token.trim(),
                nombreUsuario: nombreUsuario.trim(),
                nuevaContrasena: nuevaContrasena
            });

            if (response.data.exito) {
                setStep(3); // Éxito
                setMensaje(response.data.mensaje || 'Contraseña actualizada exitosamente');

                // Redirigir al login después de 3 segundos
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(response.data.mensaje || 'Error al actualizar la contraseña');
            }
        } catch (err) {
            console.error(err);
            if (err.response?.data?.mensaje) {
                setError(err.response.data.mensaje);
            } else {
                setError('Error al procesar la solicitud.');
            }
        } finally {
            setEnviando(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !enviando) {
            if (step === 1) handleValidarToken();
            if (step === 2) handleActualizarPassword();
        }
    };

    // --- PANTALLA DE ÉXITO (STEP 3) ---
    if (step === 3) {
        return (
            <div className="page-container">
                <Header />
                <main className="page-main">
                    <div className={containerClass} style={containerStyle}>
                        <div className={cardClass} style={{ textAlign: 'center' }}>
                            <FaCheckCircle style={{ fontSize: '60px', color: '#4CAF50', marginBottom: '20px' }} />
                            <h2 className="login-form-title">¡Contraseña Actualizada!</h2>
                            <p style={{ color: '#fff', marginTop: '15px', fontSize: '14px' }}>
                                {mensaje}
                            </p>
                            <p style={{ color: '#fff', marginTop: '10px', fontSize: '14px' }}>
                                Serás redirigido al login en unos segundos...
                            </p>
                            <button
                                className={buttonClass}
                                onClick={() => navigate('/login')}
                                style={{ marginTop: '20px' }}
                            >
                                <FaLock />
                                <span>Ir al Login</span>
                            </button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // --- FORMULARIO PRINCIPAL (STEP 1 y 2) ---
    return (
        <div className="page-container">
            <Header />

            <main className="page-main">
                <div className={containerClass} style={containerStyle}>
                    <div className={cardClass} role="form" aria-label="Formulario de reseteo de contraseña">

                        {/* Header del formulario */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                            {step === 1 && (
                                <button
                                    onClick={() => navigate('/forgot-password')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: '18px',
                                        padding: '5px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    title="Volver"
                                >
                                    <FaArrowLeft />
                                </button>
                            )}
                            <h2 className="login-form-title" style={{ margin: 0 }}>Restablecer Contraseña</h2>
                        </div>

                        {step === 1 && (
                            <p style={{ color: '#fff', marginBottom: '20px', textAlign: 'center', fontSize: '14px' }}>
                                Ingresa el código que enviamos a tu correo y tu usuario.
                            </p>
                        )}
                        {step === 2 && (
                            <p style={{ color: '#fff', marginBottom: '20px', textAlign: 'center', fontSize: '14px' }}>
                                ¡Código válido! Ahora crea tu nueva contraseña.
                            </p>
                        )}

                        {/* STEP 1: VALIDAR TOKEN */}
                        {step === 1 && (
                            <>
                                {/* Campo Usuario */}
                                <div style={{ position: 'relative', width: '100%', marginBottom: '15px' }}>
                                    <FaUser style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#666',
                                        fontSize: '16px',
                                        zIndex: 1
                                    }} />
                                    <input
                                        type="text"
                                        placeholder="Nombre de Usuario"
                                        className={inputClass}
                                        value={nombreUsuario}
                                        onChange={(e) => setNombreUsuario(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        style={{ paddingLeft: '40px' }}
                                        disabled={enviando}
                                    />
                                </div>

                                {/* Campo Token */}
                                <div style={{ position: 'relative', width: '100%', marginBottom: '20px' }}>
                                    <FaKey style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#666',
                                        fontSize: '16px',
                                        zIndex: 1
                                    }} />
                                    <input
                                        type="text"
                                        placeholder="Código de Recuperación"
                                        className={inputClass}
                                        value={token}
                                        onChange={(e) => setToken(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        style={{ paddingLeft: '40px' }}
                                        disabled={enviando}
                                        autoComplete="off"
                                    />
                                </div>

                                <button
                                    className={buttonClass}
                                    onClick={handleValidarToken}
                                    disabled={enviando}
                                    style={{ opacity: enviando ? 0.7 : 1 }}
                                >
                                    <FaCheckCircle />
                                    <span>{enviando ? 'Validando...' : 'Validar Código'}</span>
                                </button>
                            </>
                        )}

                        {/* STEP 2: NUEVA CONTRASEÑA */}
                        {step === 2 && (
                            <>
                                {/* Campos Ocultos/Visuales de Info */}
                                <div style={{
                                    marginBottom: '15px',
                                    padding: '8px',
                                    background: 'rgba(255,255,255,0.1)',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    color: '#ddd',
                                    textAlign: 'center'
                                }}>
                                    Usuario: <strong>{nombreUsuario}</strong>
                                </div>

                                {/* Campo Nueva Contraseña */}
                                <div style={{ position: 'relative', width: '100%', marginBottom: '15px' }}>
                                    <FaLock style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#666',
                                        fontSize: '16px',
                                        zIndex: 1
                                    }} />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Nueva Contraseña"
                                        className={inputClass}
                                        value={nuevaContrasena}
                                        onChange={(e) => setNuevaContrasena(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                                        disabled={enviando}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#666',
                                            display: 'flex',
                                            padding: 4,
                                            zIndex: 10,
                                        }}
                                    >
                                        {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                    </button>
                                </div>

                                {/* Campo Confirmar Contraseña */}
                                <div style={{ position: 'relative', width: '100%', marginBottom: '20px' }}>
                                    <FaLock style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#666',
                                        fontSize: '16px',
                                        zIndex: 1
                                    }} />
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Confirmar Contraseña"
                                        className={inputClass}
                                        value={confirmarContrasena}
                                        onChange={(e) => setConfirmarContrasena(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                                        disabled={enviando}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#666',
                                            display: 'flex',
                                            padding: 4,
                                            zIndex: 10,
                                        }}
                                    >
                                        {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                    </button>
                                </div>

                                <button
                                    className={buttonClass}
                                    onClick={handleActualizarPassword}
                                    disabled={enviando}
                                    style={{ opacity: enviando ? 0.7 : 1 }}
                                >
                                    <FaLock />
                                    <span>{enviando ? 'Actualizando...' : 'Actualizar Contraseña'}</span>
                                </button>
                            </>
                        )}

                        {/* Mensajes de Estado */}
                        {mensaje && (
                            <div style={{
                                marginTop: '15px',
                                padding: '12px',
                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                border: '1px solid rgba(76, 175, 80, 0.5)',
                                borderRadius: '8px',
                                color: '#4CAF50',
                                fontSize: '14px',
                                textAlign: 'center'
                            }}>
                                {mensaje}
                            </div>
                        )}

                        {error && (
                            <div style={{
                                marginTop: '15px',
                                padding: '12px',
                                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                border: '1px solid rgba(244, 67, 54, 0.5)',
                                borderRadius: '8px',
                                color: '#F44336',
                                fontSize: '14px',
                                textAlign: 'center'
                            }}>
                                {error}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}
